import { useReducer, useEffect, useCallback, useState } from "react";
import { withFestivalExperience } from "@/lib/festivalExperience";
import { toast } from "react-hot-toast";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { GETREQUESTAPI, POSTREQUESTAPI } from "@/pages/api/apiUtils";
import Pagination from "@/components/pagination/Pagination";
import PoojaImageComponent from "@/components/PoojaComponent/PoojaImageBoxComponent";
import FilterBar from "@/components/v2/FilterBar";
import ActiveFilters from "@/components/v2/ActiveFilters";
import FestivalCollection from "@/components/festival/FestivalCollection";
import {
  BACKEND_API_ROUTE,
  GET_POOJA_ITEMS,
  POOJA_WISHLIST_API,
  POOJA_CART_API,
} from "@/commons/api_routes";
import { getCookieItem, formatSlugItem } from "@/utils/utils";
import Shimmer from "@/components/AnimationComponent/Shimmer.jsx";
import { findLocLangIds } from "@/utils/locLangUtils";
import { useV2GlobalData } from "@/hooks/useV2GlobalData";
import StickyMobileContact from "@/components/v2/StickyMobileContact";
import Head from "next/head";
import { DEFAULT_SEO_VALUES } from "@/commons/constants";
import { fetchSingleSEOByItem } from "@/pages/api";
import { countCategoryPoojas } from "@/lib/seo-indexing";


const initialState = {
  poojaItems: [],
  loading: true,
  currentPage: 1,
  recordsPerPage: 12,
  totalItems: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_POOJA_ITEMS":
      return { ...state, poojaItems: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_TOTAL_ITEMS":
      return { ...state, totalItems: action.payload };
    default:
      return state;
  }
}

const CategoryPage = ({ seoData, noIndex = false }) => {
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { slug, language: routerLanguage, location: routerLocation } = router.query;
  const { locations, languages } = useSelector((state) => state.locationandlanguages || {});

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([501, 110000]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [sortBy, setSortBy] = useState("popular");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isUrlInitialized, setIsUrlInitialized] = useState(false);

  const { data: v2Data } = useV2GlobalData();
  const categoriesList = v2Data?.generalCategories || [];

  // Initialize state from URL
  useEffect(() => {
    if (router.isReady && !isUrlInitialized) {
      if (router.query.categories) setSelectedCategories(router.query.categories.split(','));
      if (router.query.minPrice || router.query.maxPrice) {
        setPriceRange([
          router.query.minPrice ? parseInt(router.query.minPrice) : 3500,
          router.query.maxPrice ? parseInt(router.query.maxPrice) : 21000
        ]);
      }
      if (router.query.durations) setSelectedDurations(router.query.durations.split(','));
      if (router.query.sort) setSortBy(router.query.sort);
      if (router.query.q) {
        setSearch(router.query.q);
        setDebouncedSearch(router.query.q);
      }
      if (router.query.page) {
        dispatch({ type: "SET_CURRENT_PAGE", payload: parseInt(router.query.page) });
      }
      setIsUrlInitialized(true);
    }
  }, [router.isReady, router.query, isUrlInitialized]);

  // Sync state to URL
  useEffect(() => {
    if (!isUrlInitialized) return;

    const query = { ...router.query };
    
    if (selectedCategories.length) query.categories = selectedCategories.join(",");
    else delete query.categories;

    if (priceRange[0] !== 3500 || priceRange[1] !== 21000) {
      query.minPrice = priceRange[0];
      query.maxPrice = priceRange[1];
    } else {
      delete query.minPrice;
      delete query.maxPrice;
    }

    if (selectedDurations.length) query.durations = selectedDurations.join(",");
    else delete query.durations;

    if (sortBy && sortBy !== "popular") query.sort = sortBy;
    else delete query.sort;

    if (search) query.q = search;
    else delete query.q;

    if (state.currentPage > 1) query.page = state.currentPage;
    else delete query.page;

    router.push({
      pathname: router.pathname,
      query: query
    }, undefined, { shallow: true });

  }, [selectedCategories, priceRange, selectedDurations, sortBy, search, state.currentPage, isUrlInitialized]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const handleCategoryToggle = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleDurationToggle = (dur) => {
    setSelectedDurations(prev => prev.includes(dur) ? prev.filter(d => d !== dur) : [...prev, dur]);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleClearAll = () => {
    setSelectedCategories([]);
    setSelectedDurations([]);
    setPriceRange([3500, 21000]);
    setSearch("");
    setDebouncedSearch("");
    setSortBy("popular");
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleSearchChange = (val) => {
    if (search !== val) {
      setSearch(val);
      if (val === "") {
        setDebouncedSearch("");
      }
      dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
    }
  };
  const handlePriceChange = (val) => {
    setPriceRange(val);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleSortChange = (val) => {
    if (sortBy !== val) {
      setSortBy(val);
      dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
    }
  };

  const { currentPage, recordsPerPage, poojaItems, totalItems, loading } = state;
  const nPages = Math.ceil(totalItems / recordsPerPage);

  const findLocationAndLanguageIds = useCallback(() => {
    return findLocLangIds(locations, languages, routerLocation, routerLanguage);
  }, [routerLocation, routerLanguage, locations, languages]);

  const postUserCartApi = async (data) => {
    try {
      const { locationId, languageId } = findLocationAndLanguageIds()
      if (!locationId || !languageId) {
        toast.error("Location or language not found");
        return;
      }

      const cartApiUrl = BACKEND_API_ROUTE + POOJA_CART_API;
      const accessToken = getCookieItem("accessToken");
      const payload = {
        poojaId: data._id,
        language: languageId,
        location: locationId,
      };

      const cartResponse = await POSTREQUESTAPI(cartApiUrl, payload, accessToken);

      if (cartResponse.status === 201 && cartResponse.data.statusCode === 200) {
        toast.success(cartResponse.data.message);
      } else {
        toast.error(cartResponse.data.error || "Failed to add item to cart");
      }
    } catch (error) {
      toast.error("Error adding to cart. Please try again.");
    }
  };

  const postUserWishlistApi = async (data) => {
    try {
      const wishlistApiUrl = BACKEND_API_ROUTE + POOJA_WISHLIST_API;
      const accessToken = getCookieItem("accessToken");

      if (!accessToken) {
        toast("Please login to add item to wishlist");
        return;
      }

      const payload = {
        poojaId: data._id,
      };

      const wishlistResponse = await POSTREQUESTAPI(
        wishlistApiUrl,
        payload,
        accessToken
      );

      if (wishlistResponse.status === 201 && wishlistResponse.data.statusCode === 200) {
        toast.success(wishlistResponse.data.message);
      } else {
        toast.error(wishlistResponse.data.error || "Failed to add item to wishlist");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast("Please login to add item to wishlist");
      } else {
        toast.error("Error adding to wishlist. Please try again.");
      }
    }
  };

  const fetchPoojaItems = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const { locationId, languageId } = findLocationAndLanguageIds();
      
      const categoryName = slug?.split('?')[0];

      if (!locationId || !languageId || !categoryName) {
        dispatch({ type: "SET_LOADING", payload: false });
        dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
        return;
      }

      let url = "";

      if (debouncedSearch && debouncedSearch.trim() !== "") {
        // Here we ideally want to scope the search to the category, 
        // but the find API doesn't accept a category array directly.
        // If it's heavily needed, this is the limit of the `find` endpoint.
        url = `${BACKEND_API_ROUTE}/pooja/find?t=${encodeURIComponent(debouncedSearch.trim())}&language=${languageId}&location=${locationId}&p=false`;
      } else {
        const queryParams = new URLSearchParams({
          page: currentPage - 1,
          pageSize: recordsPerPage,
          sort: sortBy.replace("-", "_"),
          projection: "PLP",
        });

        // Add price min/max filters
        queryParams.append("minPrice", priceRange[0]);
        queryParams.append("maxPrice", priceRange[1]);

        // Add categories (hardcoded slug + selected)
        queryParams.append("category", categoryName);
        selectedCategories.forEach(cat => {
          if (cat !== categoryName) queryParams.append("category", cat);
        });

        queryParams.append("language", languageId);
        queryParams.append("location", locationId);
        url = `${BACKEND_API_ROUTE}${GET_POOJA_ITEMS}?${queryParams}`;
      }

      const response = await GETREQUESTAPI(url);

      if (response.status === 200) {
        const data = response.data;
        if (data.statusCode === 200) {
          dispatch({ type: "SET_POOJA_ITEMS", payload: data.body || [] });
          dispatch({ type: "SET_TOTAL_ITEMS", payload: data.totalCount || 0 });
        } else {
          toast.error("No Matching Pooja Found!");
          dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
        }
      } else {
        toast.error("Something Went Wrong!");
        dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
      }
    } catch (error) {
      toast.error("Error fetching pooja items. Please try again.");
      dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [currentPage, recordsPerPage, findLocationAndLanguageIds, debouncedSearch, priceRange, selectedCategories, sortBy, slug]);

  useEffect(() => {
    if (router.isReady && isUrlInitialized) {
      fetchPoojaItems();
    }
  }, [router.isReady, isUrlInitialized, fetchPoojaItems]);

  const handlePageChange = (pageNumber) => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: pageNumber });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePoojaItemClick = (poojaData) => {
    const poojaPath = `/${routerLanguage?.toLowerCase()}/${routerLocation?.toLowerCase()}/pooja/${poojaData.slug}`;
    router.push(poojaPath);
  };

  const renderBreadcrumbs = () => {
    const breadcrumbs = [];

    if (routerLocation) {
      breadcrumbs.push(routerLocation.replace(/-/g, " "));
    }

    if (routerLanguage) {
      breadcrumbs.push(routerLanguage.replace(/-/g, " "));
    }

    if (slug) {
      breadcrumbs.push(formatSlugItem(slug?.split('?')[0]));
    }

    breadcrumbs.push("Pooja Services");

    return breadcrumbs.join(" / ");
  };

  const canonical = process.env.NEXT_PUBLIC_SITE_URL + router.asPath?.toLowerCase();

  return (
    <>
      <Head>
        <title>{seoData?.title}</title>
        {noIndex && <meta name="robots" content="noindex, follow" />}
        <meta name="description" content={seoData?.description} />
        <meta property="og:image" content={seoData?.imageLink} />
        <link rel="canonical" href={canonical} />
        <meta name="og:title" content={seoData?.title} />
        <meta name="og:description" content={seoData?.description} />
        <meta name="og:url" content={process.env.NEXT_PUBLIC_SITE_URL} />
      </Head>

      <div className="bg-background min-h-screen">
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 pt-4 pb-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            {renderBreadcrumbs()}
          </p>
        </div>

        {/* Page Title with result count */}
        <div className="container mx-auto px-4 pb-2">
          <h1 className="text-xl md:text-2xl font-bold text-foreground capitalize">
            {formatSlugItem(slug?.split('?')[0])} Pooja Services in {routerLocation?.replace(/-/g, " ")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Showing {totalItems} sacred rituals available
          </p>
        </div>

        {/* Filter Bar */}
        <div className="container mx-auto px-4">
          <FilterBar
            categories={categoriesList}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            priceRange={priceRange}
            onPriceChange={handlePriceChange}
            selectedDurations={selectedDurations}
            onDurationToggle={handleDurationToggle}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            search={search}
            onSearchChange={handleSearchChange}
            onClearAll={handleClearAll}
          />
        </div>

        {/* Active filter chips */}
        <div className="container mx-auto px-4 py-2">
          <ActiveFilters
            search={search}
            onClearSearch={() => handleSearchChange("")}
            categories={selectedCategories}
            onRemoveCategory={handleCategoryToggle}
            durations={selectedDurations}
            onRemoveDuration={handleDurationToggle}
            priceRange={priceRange}
            onResetPrice={() => handlePriceChange([3500, 21000])}
            onClearAll={handleClearAll}
          />
        </div>

        {/* Festival Experience — contextual collection (renders nothing when no campaign) */}
        <div className="container mx-auto px-4 pt-2">
          <FestivalCollection slotKey="category.context" />
        </div>

        <section className="px-4 py-4 container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 2xl:gap-6">
            {loading ? (
              Array.from({ length: 12 }, (_, index) => (
                <Shimmer key={`shimmer-${index}`} />
              ))
            ) : poojaItems.length > 0 ? (
              poojaItems.map((boxData, index) => (
                <div
                  key={`pooja-item-${boxData._id || index}`}
                  className="max-w-[500px]"
                >
                  <PoojaImageComponent
                    imageUrl={boxData.media?.[0]?.mediaUrl}
                    altText={boxData.name}
                    poojaData={boxData}
                    postUserWishlistApi={postUserWishlistApi}
                    handlePoojaItemClick={handlePoojaItemClick}
                    postUserCartApi={postUserCartApi}
                  />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-10">
                No pooja items found for the selected criteria
              </div>
            )}
          </div>

          {poojaItems.length > 0 && nPages > 1 && (
            <div className="flex flex-row justify-end items-center font-bastoni text-4xl text-Default mt-6">
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </section>
      </div>
      <StickyMobileContact />
    </>
  );
};

async function getPageServerSideProps(context) {
  const { params, res: response } = context;
  const { slug, language, location } = params;
  const categoryName = slug?.split('?')[0];

  // Legacy categories and some city/language combos list zero poojas — keep
  // those empty pages out of search. A failed count (null) stays indexable.
  const [seoResult, poojaCount] = await Promise.all([
    fetchSingleSEOByItem(`/category?categoryName=${categoryName}&language=${language}&location=${location}`)
      .catch((error) => {
        console.error('Error fetching category SEO details:', error.message);
        return null;
      }),
    countCategoryPoojas(categoryName, language, location),
  ]);
  const noIndex = poojaCount === 0;
  if (noIndex) {
    response.setHeader('X-Robots-Tag', 'noindex, follow');
  }

  const res = seoResult;
  if(res && res.status === 200 && res.data && res.data.statusCode === 200 && res.data.body){
    return {
      props: { seoData: res?.data?.body, noIndex },
    }
  }
  return {
    props: { seoData: DEFAULT_SEO_VALUES, noIndex },
  }
}

export default CategoryPage;

// Festival layer resolved alongside the page data (renders nothing if no campaign).
export const getServerSideProps = withFestivalExperience("category", getPageServerSideProps);
